import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
  InLineIntegration,
  CreateEdgeIntegrationRequest,
  InLineIntegrationUpdateData,
  GenerateFoundryApiKeyRequest,
  FoundryApiKeyResponse,
  FoundryApiKeyMetadata,
  CheckActiveKeyResponse,
  Provider,
} from 'src/app/models/api/inLineIntegration/inLineIntegration.model';
import { MicroserviceHttpService } from '../http/microservice-http.service';
import { LoggerService } from '../../infra/logger/logger.service';
import { environment } from 'src/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class InLineIntegrationsHttpService {
  private readonly _baseUrl: string = 'integrations';
  private readonly _httpService: MicroserviceHttpService;

  constructor(
    private readonly _httpClient: HttpClient,
    private readonly _logger: LoggerService
  ) {
    this._httpService = new MicroserviceHttpService(
      this._httpClient,
      'ai-edge-manager' as keyof typeof environment.services,
      this._logger
    );
  }

  /**
   * GET /api/v1/integrations
   * Retrieves all inline integrations
   */
  public getInLineIntegrations(): Observable<InLineIntegration[]> {
    return this._httpService.get<InLineIntegration[]>(this._baseUrl);
  }

  /**
   * POST /api/v1/integrations
   * Creates a new inline integration
   */
  public createInLineIntegration(request: CreateEdgeIntegrationRequest): Observable<InLineIntegration> {
    return this._httpService.post<CreateEdgeIntegrationRequest, InLineIntegration>(this._baseUrl, request);
  }

  /**
   * PUT /api/v1/integrations/:integrationId
   * Updates an existing inline integration
   */
  public updateInLineIntegration(
    integrationId: string,
    updateData: InLineIntegrationUpdateData
  ): Observable<InLineIntegration> {
    const url = `${this._baseUrl}/${integrationId}`;
    return this._httpService.put<InLineIntegration, InLineIntegrationUpdateData>(url, updateData);
  }

  /**
   * GET /api/v1/integrations/byIntegrationId/:relatedIntegrationId
   * Retrieves a specific inline integration by ID
   * Optional provider param filters by provider type
   */
  public getInLineIntegrationByRelatedIntegrationId(
    relatedIntegrationId: string,
    provider?: Provider
  ): Observable<InLineIntegration> {
    const providerParam = provider ? `?provider=${provider}` : '';
    const url = `${this._baseUrl}/byIntegrationId/${relatedIntegrationId}${providerParam}`;
    return this._httpService.get<InLineIntegration>(url);
  }

  /**
   * DELETE /api/v1/integrations/:integrationId
   * Deletes a specific inline integration by ID
   * Optional provider param for provider-specific deletion
   */
  public deleteInLineIntegration(integrationId: string, provider?: Provider): Observable<void> {
    const providerParam = provider ? `?provider=${provider}` : '';
    const url = `${this._baseUrl}/${integrationId}${providerParam}`;
    return this._httpService.delete<void>(url);
  }

  public getAllInLineIntegrations(): Observable<InLineIntegration[]> {
    return this._httpService.get<InLineIntegration[]>(this._baseUrl);
  }

  public generateFoundryApiKey(request: GenerateFoundryApiKeyRequest): Observable<FoundryApiKeyResponse> {
    return this._httpService.post<GenerateFoundryApiKeyRequest, FoundryApiKeyResponse>('foundry-api-key', request);
  }

  public revokeFoundryApiKey(accountId: string, integrationId: string): Observable<FoundryApiKeyMetadata> {
    const url = `foundry-api-keys/${accountId}/${integrationId}/revoke`;
    return this._httpService.post<undefined, FoundryApiKeyMetadata>(url, undefined);
  }

  public checkFoundryApiKeyExists(accountId: string, integrationId: string): Observable<CheckActiveKeyResponse> {
    const url = `foundry-api-keys/${accountId}/${integrationId}/active`;
    return this._httpService.get<CheckActiveKeyResponse>(url);
  }
}
